import { CSSProperties, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { BrandGlyph, MODULE_COLOR } from '../lib/branding';

/* ============================================================
   NativeSignIn
   ------------------------------------------------------------
   In-app email/password sign-in AND sign-up used by the native
   iOS shell (Capacitor build). Used to bounce to
   plan.cadtomie.com for sign-up, but Apple expects native apps
   to support account creation in-app (Guideline 4.0 / 4.8) and
   the round-trip to the planner is poor UX anyway.

   Three states drive the UI:
     • 'signin'  — email + password sign-in
     • 'signup'  — email + password + confirm-password sign-up
     • 'verify-sent' — post-signup success card asking the user
       to click the verification link in their email

   The cross-subdomain auth cookie the planner uses doesn't
   reach into `capacitor://localhost`, so Supabase persists the
   session in WKWebView's localStorage instead — scoped per
   app, surviving relaunches. Identical session model as the
   pre-existing sign-in flow.

   Email verification is REQUIRED by the backend's
   REQUIRE_EMAIL_CONFIRMATION gate AND by RequireSession's
   `email_confirmed_at` check, so we show the verify-sent
   screen whenever Supabase returns `session: null` from
   signUp() — that's Supabase's signal that confirmation is
   pending.
   ============================================================ */

type Mode = 'signin' | 'signup' | 'verify-sent';

export function NativeSignIn() {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSignIn(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (authError) throw authError;
      // On success, useSession's listener fires and RequireSession
      // re-renders with the gated children.
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign-in failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignUp(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;

    setError(null);

    // Cheap client-side checks so the user gets a fast answer
    // before we round-trip to Supabase.
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
      });
      if (authError) throw authError;

      // Supabase returns `session: null` when email confirmation is
      // required (which it is in our project). Show the verify-sent
      // screen so the user knows to check their inbox.
      // If a session IS returned (confirmation disabled), the auth
      // listener fires and RequireSession lifts the gate.
      if (!data.session) {
        setMode('verify-sent');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign-up failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    // Clear sensitive fields when switching — leaving them
    // populated after a failed attempt has surprised users.
    setPassword('');
    setConfirm('');
  }

  if (mode === 'verify-sent') {
    return (
      <VerifyEmailSent
        email={email}
        onBack={() => switchMode('signin')}
      />
    );
  }

  const isSignUp = mode === 'signup';

  return (
    <main className="safe-top safe-bottom" style={pageStyle}>
      <Atmosphere />
      <form
        style={cardStyle}
        onSubmit={isSignUp ? handleSignUp : handleSignIn}
        noValidate
      >
        <BrandGlyph size={48} color={MODULE_COLOR.angle} />
        <h1 style={titleStyle}>
          {isSignUp ? 'Create your account' : 'Sign in to Intraop'}
        </h1>
        <p style={subtitleStyle}>
          {isSignUp
            ? 'Free Educational access. Upgrade to Pro from inside the app for the full measurement workflow.'
            : 'One CADtomie account covers both the planner and Intraop.'}
        </p>

        <label style={fieldLabelStyle}>
          <span className="sr-only">Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete={isSignUp ? 'email' : 'username'}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </label>

        <label style={fieldLabelStyle}>
          <span className="sr-only">Password</span>
          <input
            type="password"
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
            placeholder={
              isSignUp ? 'Password (min. 6 characters)' : 'Password'
            }
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </label>

        {isSignUp && (
          <label style={fieldLabelStyle}>
            <span className="sr-only">Confirm password</span>
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Confirm password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              style={inputStyle}
            />
          </label>
        )}

        {error && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={
            submitting || !email || !password || (isSignUp && !confirm)
          }
          style={primaryBtnStyle}
        >
          {submitting
            ? isSignUp
              ? 'Creating account…'
              : 'Signing in…'
            : isSignUp
              ? 'Create account'
              : 'Sign in'}
        </button>

        <p style={hintStyle}>
          {isSignUp ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => switchMode('signin')}
                style={inlineToggleStyle}
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              New here?{' '}
              <button
                type="button"
                onClick={() => switchMode('signup')}
                style={inlineToggleStyle}
              >
                Create an account
              </button>
            </>
          )}
        </p>

        {/* Legal links — App Store Review requires these to be reachable
            before sign-in / sign-up. Stays low-noise in the layout. */}
        <p style={legalLineStyle}>
          {isSignUp
            ? 'By creating an account, you accept our '
            : 'By signing in, you accept our '}
          <Link to="/terms" style={legalLinkStyle}>Terms</Link>
          {' · '}
          <Link to="/privacy" style={legalLinkStyle}>Privacy</Link>
          {' · '}
          <Link to="/impressum" style={legalLinkStyle}>Impressum</Link>
        </p>
      </form>
    </main>
  );
}

/* ─────────────────── Verify-email-sent screen ─────────────────── */

function VerifyEmailSent({
  email,
  onBack,
}: {
  email: string;
  onBack: () => void;
}) {
  return (
    <main className="safe-top safe-bottom" style={pageStyle}>
      <Atmosphere />
      <div style={cardStyle}>
        <BrandGlyph size={48} color={MODULE_COLOR.height} />
        <h1 style={titleStyle}>Check your email</h1>
        <p style={subtitleStyle}>
          We sent a verification link to{' '}
          <strong style={{ color: 'var(--text)' }}>{email}</strong>. Click the
          link in the email, then come back and sign in here.
        </p>
        <p style={subtitleStyle}>
          Didn't get the email? Check your spam folder, or wait a minute and
          try again.
        </p>
        <button type="button" style={primaryBtnStyle} onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </main>
  );
}

/* ─────────────────── Background atmosphere ─────────────────── */

function Atmosphere() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(60% 40% at 50% 0%, rgba(124,196,255,0.08), transparent 60%), ' +
          'radial-gradient(70% 50% at 50% 100%, rgba(124,196,255,0.04), transparent 60%)',
      }}
    />
  );
}

/* ─────────────────── Styles ─────────────────── */

const pageStyle: CSSProperties = {
  position: 'relative',
  minHeight: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: 'var(--bg)',
};

const cardStyle: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  maxWidth: 420,
  width: '100%',
  background: 'var(--surface-strong)',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius-lg)',
  padding: '36px 28px 28px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 14,
  textAlign: 'center',
  boxShadow: 'var(--shadow)',
};

const titleStyle: CSSProperties = {
  margin: '8px 0 0 0',
  color: 'var(--text)',
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '-0.02em',
};

const subtitleStyle: CSSProperties = {
  margin: '0 0 8px 0',
  color: 'var(--text-dim)',
  fontSize: 15,
  lineHeight: 1.5,
};

const fieldLabelStyle: CSSProperties = {
  display: 'block',
  textAlign: 'left',
};

// font-size: 16px is the iOS Safari/WKWebView threshold below which the
// keyboard zoom-in kicks in on focus. Keep at >=16 to avoid the layout jump.
const inputStyle: CSSProperties = {
  width: '100%',
  background: 'rgba(0,0,0,0.35)',
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius)',
  color: 'var(--text)',
  padding: '14px 16px',
  fontSize: 16,
  lineHeight: 1.3,
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
  font: 'inherit',
};

const errorStyle: CSSProperties = {
  margin: 0,
  color: 'var(--danger)',
  fontSize: 13,
  textAlign: 'left',
  lineHeight: 1.4,
};

const primaryBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginTop: 4,
  background: 'var(--accent)',
  color: '#0a0a0a',
  border: 'none',
  borderRadius: 999,
  padding: '14px 24px',
  minHeight: 'var(--tap-pref)', // 64px — gloved-finger-friendly
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
  font: 'inherit',
};

const hintStyle: CSSProperties = {
  margin: '4px 0 0 0',
  color: 'var(--text-muted)',
  fontSize: 13,
  lineHeight: 1.45,
  textAlign: 'center',
};

/* Inline toggle between sign-in and sign-up modes. Styled as a
   link but rendered as <button> for accessibility (it's an
   action, not a navigation). */
const inlineToggleStyle: CSSProperties = {
  display: 'inline',
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  color: 'var(--text)',
  fontWeight: 600,
  fontSize: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  cursor: 'pointer',
  font: 'inherit',
  WebkitTapHighlightColor: 'transparent',
};

const legalLineStyle: CSSProperties = {
  margin: '8px 0 0 0',
  color: 'var(--text-muted)',
  fontSize: 11,
  lineHeight: 1.5,
  textAlign: 'center',
};

const legalLinkStyle: CSSProperties = {
  color: 'var(--text-dim)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};
